import { TIME_GUTTER_CLASS } from '../constants';
import { DAY_NAMES_FULL, MONTH_NAMES } from '../types';
import type { EventInstance, TemperatureUnit, TimeFormat } from '../types';
import type { WeatherDay } from '../types';
import { getAllDayEvents, isToday, formatDateKey, formatTemperature } from '../utils';
import { AllDayRow } from './AllDayRow';
import { useScrollbarWidth } from '../hooks/useScrollbarWidth';
import { TimeGrid } from './TimeGrid';
import { WeatherIcon } from './WeatherIcon';

interface DayViewProps {
  currentDate: Date;
  events: EventInstance[];
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onSelectRange: (start: Date, end: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  draggingEventId?: string | null;
  dragOffset?: { x: number; y: number } | null;
  onEventDragStart?: (eventId: string, e: React.MouseEvent) => void;
  weather?: Map<string, WeatherDay>;
  temperatureUnit?: TemperatureUnit;
  hourHeightPx?: number;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
  snapMinutes?: number;
  defaultDurationMinutes?: number;
}

export function DayView({
  currentDate,
  events,
  workingHoursStart,
  timeFormat,
  onSelectRange,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  draggingEventId,
  dragOffset,
  onEventDragStart,
  weather,
  temperatureUnit,
  hourHeightPx,
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
  snapMinutes,
  defaultDurationMinutes,
}: DayViewProps) {
  const today = isToday(currentDate);
  const scrollbarGutterPx = useScrollbarWidth();
  const allDayColumns = [{ date: currentDate, events: getAllDayEvents(events, currentDate) }];
  const dateKey = formatDateKey(currentDate);
  const dayWeather = weather?.get(dateKey);

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Day header */}
      <div className="flex border-b">
        <div className={TIME_GUTTER_CLASS} />
        <div className={`flex-1 min-w-0 text-center py-2 ${today ? 'bg-primary/5' : ''}`}>
          <div className="text-xs text-muted-foreground uppercase truncate">
            {DAY_NAMES_FULL[currentDate.getDay()]}
          </div>
          <div
            className={`text-lg font-semibold ${
              today
                ? 'bg-primary text-primary-foreground rounded-full w-9 h-9 flex items-center justify-center mx-auto'
                : ''
            }`}
          >
            {currentDate.getDate()}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
          {dayWeather && (
            <div className="flex items-center justify-center gap-1 mt-0.5">
              <WeatherIcon code={dayWeather.weatherCode} size={16} />
              <span className="text-xs text-muted-foreground">
                {formatTemperature(dayWeather.tempMin, temperatureUnit)}–
                {formatTemperature(dayWeather.tempMax, temperatureUnit)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* All-day row */}
      <AllDayRow
        dayColumns={allDayColumns}
        scrollbarGutterPx={scrollbarGutterPx}
        onClickEvent={onClickEvent}
        eventColorPriority={eventColorPriority}
      />

      {/* Time grid (single column) */}
      <TimeGrid
        days={[currentDate]}
        events={events}
        workingHoursStart={workingHoursStart}
        timeFormat={timeFormat}
        onSelectRange={onSelectRange}
        onClickEvent={onClickEvent}
        resizingEventId={resizingEventId}
        resizeHeight={resizeHeight}
        onResizeStart={onResizeStart}
        draggingEventId={draggingEventId}
        dragOffset={dragOffset}
        onEventDragStart={onEventDragStart}
        hourHeightPx={hourHeightPx}
        dimPastEvents={dimPastEvents}
        eventColorPriority={eventColorPriority}
        showConflictWarnings={showConflictWarnings}
        minBreakBetweenEvents={minBreakBetweenEvents}
        snapMinutes={snapMinutes}
        defaultDurationMinutes={defaultDurationMinutes}
      />
    </div>
  );
}
